<template>
  <v-card class="shops-table">
    <v-card-title class="shops-table__title">Shops im Vergleich <span class="shops-table__hint">Klick auf eine Kachel zeigt die Details</span></v-card-title>
    <!-- Kacheln, 2 je Reihe (Malte 02.11.2026): Kernzahlen auf einen Blick, Details per Klick (nur eine Kachel offen) -->
    <div class="shops-grid">
      <div
        v-for="item in rows"
        :key="item.shopCode"
        class="tile"
        :class="{ 'tile--open': expandedCode === item.shopCode }"
        @click="toggle(item.shopCode)"
      >
        <div class="tile__head">
          <span class="tile__name"><span class="shops-table__dot" :style="{ background: item.color }" />{{ item.domain }}</span>
          <span class="tile__orders">{{ item.orders }} Orders</span>
          <v-icon small class="tile__chevron">{{ expandedCode === item.shopCode ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <div class="tile__facts">
          <div class="fact"><span class="fact__label">Umsatz</span><span class="fact__value">{{ formatEuro(item.revenue) }}</span></div>
          <div class="fact"><span class="fact__label">Gewinn</span><span class="fact__value" :class="item.profit < 0 ? 'num--bad' : 'num--good'">{{ formatEuro(item.profit) }}</span></div>
          <div class="fact"><span class="fact__label">Marge</span><span class="fact__value">{{ item.margin.toFixed(1).replace(".", ",") }} %</span></div>
          <div class="fact"><span class="fact__label">ROAS</span><span class="fact__value">{{ item.roas ? item.roas.toFixed(2).replace(".", ",") : "—" }}</span></div>
        </div>
        <div v-if="expandedCode === item.shopCode" class="tile__detail" @click.stop>
          <ShopStatisticCard
            :shop-statistic="item.raw"
            :comparison-shop-statistic="comparisonForShop(item.shopCode)"
          />
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import ShopStatisticCard from "@/components/Statistics/StatisticByShops/ShopStatisticCard";
import { statisticsColors } from "@/config";
import { getDecimalNumberString } from "@/utils";

export default {
  name: "ShopsTable",
  components: { ShopStatisticCard },
  props: {
    data: { type: Array, default: () => [] },
    comparisonData: { type: Array, default: null },
  },
  data() {
    return {
      expandedCode: null,
    };
  },
  computed: {
    rows() {
      return [...this.data].map((shop) => {
        // Nur numerische Kostenfelder summieren — schützt vor Map-Feldern
        // (z. B. byCountry in Alt-Sync-Docs), die sonst als "[object Object]"
        // in die Summe konkateniert würden
        const costs = Object.keys(shop.costs || {}).reduce(
          (sum, key) => sum + (typeof shop.costs[key] === "number" ? shop.costs[key] : 0), 0
        );
        const revenue = shop.revenue?.total || 0;
        const adSpend = shop.costs?.advertisement || 0;
        return {
          shopCode: shop.shopCode,
          domain: shop.domain,
          color: statisticsColors[shop.shopCode] || "#9ca3af",
          revenue,
          costs,
          profit: revenue - costs,
          margin: revenue ? ((revenue - costs) / revenue) * 100 : 0,
          roas: adSpend ? revenue / adSpend : 0,
          orders: shop.totalOrders || 0,
          raw: shop,
        };
      }).sort((a, b) => b.revenue - a.revenue);
    },
  },
  methods: {
    toggle(code) {
      this.expandedCode = this.expandedCode === code ? null : code;
    },
    formatEuro(value) {
      return `${getDecimalNumberString(value || 0, 2)} €`;
    },
    comparisonForShop(shopCode) {
      if (!this.comparisonData) return null;
      return this.comparisonData.find((shop) => shop.shopCode === shopCode) || null;
    },
  },
};
</script>

<style lang="scss" scoped>
.shops-table {
  border-radius: 12px !important;
  &__title { font-size: 16px; font-weight: 700; padding-bottom: 4px; display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
  &__hint { font-size: 12px; font-weight: 400; color: rgba(0, 0, 0, 0.5); }
  &__dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 8px; }
}
.shops-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 4px 16px 16px; }
.tile {
  border: 1px solid rgba(0, 0, 0, 0.07); border-radius: 12px; padding: 12px 14px; cursor: pointer; background: #fff;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
  &:hover { border-color: rgba(0, 0, 0, 0.18); box-shadow: 0 4px 14px rgba(16, 24, 40, 0.06); }
  &--open { grid-column: 1 / -1; border-color: rgba(0, 0, 0, 0.18); cursor: default; }
  &__head { display: flex; align-items: center; gap: 10px; }
  &__name { display: inline-flex; align-items: center; font-weight: 700; font-size: 14px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__orders { margin-left: auto; font-size: 12px; color: rgba(0, 0, 0, 0.5); white-space: nowrap; }
  &__chevron { color: rgba(0, 0, 0, 0.4); }
  &__facts { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 10px; }
  &__detail { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0, 0, 0, 0.06); background: #f8f9fb; border-radius: 10px; padding: 12px; }
}
.fact { display: flex; flex-direction: column; gap: 2px; min-width: 0;
  &__label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.5); }
  &__value { font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
}
.num--good { color: #1b7f4d; }
.num--bad { color: #c23434; }
@media (max-width: 760px) {
  .shops-grid { grid-template-columns: 1fr; padding: 4px 10px 12px; }
  .tile__facts { grid-template-columns: repeat(2, 1fr); }
}
</style>
